import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../i18n';
import api from '../../utils/api';

// Header bell with unread badge. Known notification types are localized from
// type + data; announcements carry free text from the admin and show as-is.
function notifText(n, t) {
  switch (n.type) {
    case 'trial_extended':
      return t('notif.trialExtended', { days: n.data?.days ?? '?' });
    case 'trial_reminder':
      return n.data?.stage === 'grace'
        ? t('notif.trialGrace', { days: n.data?.daysLeft ?? '?' })
        : t('notif.trialReminder', { days: n.data?.daysLeft ?? '?' });
    case 'payment_failed':
      return t('notif.paymentFailed', { days: n.data?.graceDays ?? 7 });
    case 'payment_recovered':
      return t('notif.paymentRecovered');
    case 'payment_receipt':
      return t('notif.paymentReceipt', {
        amount: `${String(n.data?.currency || 'MYR').toUpperCase()} ${Number(n.data?.amount || 0).toFixed(2)}`,
      });
    case 'support_reply':
      return t('notif.supportReply', { n: n.data?.caseNumber || '' });
    case 'support_resolved':
      return t('notif.supportResolved', { n: n.data?.caseNumber || '' });
    case 'support_case':
      return t('notif.supportCase', { n: n.data?.caseNumber || '', from: n.data?.from || '' });
    case 'support_user_reply':
      return t('notif.supportUserReply', { n: n.data?.caseNumber || '', from: n.data?.from || '' });
    case 'recurring_issued':
      return n.data?.error
        ? t('notif.recurringIssuedError', { n: n.data?.docNumber || '', error: n.data.error })
        : t(n.data?.emailed ? 'notif.recurringIssuedSent' : 'notif.recurringIssued', { n: n.data?.docNumber || '', client: n.data?.client || '' });
    case 'invoices_overdue':
      return n.data?.count > 1
        ? t('notif.invoicesOverdue', { count: n.data.count })
        : t('notif.invoiceOverdue', { n: n.data?.docNumber || '' });
    case 'einvoice_valid':
      return t('notif.einvoiceValid', { n: n.data?.docNumber || '' });
    case 'einvoice_invalid':
      return t('notif.einvoiceInvalid', { n: n.data?.docNumber || '' });
    case 'doc_viewed':
      return t('notif.docViewed', { n: n.data?.docNumber || '', client: n.data?.client || '' });
    case 'quote_accepted':
      return t('notif.quoteAccepted', { n: n.data?.docNumber || '', client: n.data?.client || '' });
    case 'quote_rejected':
      return t('notif.quoteRejected', { n: n.data?.docNumber || '', client: n.data?.client || '' });
    default:
      return n.body || n.title || n.type;
  }
}

// Where clicking a notification takes you (null = not clickable)
function notifLink(n) {
  if (n.data?.docId) return `/documents/${n.data.docId}`;
  if (n.type === 'invoices_overdue') return '/invoices';
  const id = n.data?.caseId;
  if (!id) return null;
  if (n.type === 'support_reply' || n.type === 'support_resolved') return `/support/${id}`;
  if (n.type === 'support_case' || n.type === 'support_user_reply') return `/admin/support/${id}`;
  return null;
}

function timeAgo(date, t) {
  const mins = Math.floor((Date.now() - new Date(date)) / 60000);
  if (mins < 1) return t('notif.justNow');
  if (mins < 60) return t('notif.minutesAgo', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('notif.hoursAgo', { n: hours });
  return t('notif.daysAgo', { n: Math.floor(hours / 24) });
}

const ICONS = {
  trial_extended: '🎉',
  trial_reminder: '⏳',
  payment_failed: '⚠️',
  payment_recovered: '✅',
  payment_receipt: '💳',
  announcement: '📢',
  support_reply: '🎧',
  support_resolved: '✅',
  support_case: '🎧',
  support_user_reply: '💬',
  doc_viewed: '👁',
  einvoice_valid: '🏛',
  einvoice_invalid: '⚠️',
  recurring_issued: '🔁',
  invoices_overdue: '⏰',
  quote_accepted: '🤝',
  quote_rejected: '✕',
};

export default function NotificationBell() {
  const { t } = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  // Broadcast the count so Layout can dot the mobile ☰ button without a second fetch
  const announce = n => window.dispatchEvent(new CustomEvent('invio:unread', { detail: n }));

  const load = () =>
    api.get('/notifications')
      .then(r => {
        setItems(r.data.items || []);
        setUnread(r.data.unread || 0);
        announce(r.data.unread || 0);
      })
      .catch(() => {});

  useEffect(() => { load(); }, []);

  // Close when clicking anywhere outside the panel
  useEffect(() => {
    if (!open) return;
    const onDown = e => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      load();
      if (unread > 0) {
        setUnread(0);
        announce(0);
        api.post('/notifications/read-all').catch(() => {});
      }
    }
  };

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button onClick={toggle} aria-label={t('notif.title')} title={t('notif.title')} style={{
        background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
        padding: '6px 10px', cursor: 'pointer', color: 'var(--text)',
        fontSize: 14, minWidth: 34, fontFamily: 'inherit', position: 'relative',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -6, insetInlineEnd: -6,
            background: 'var(--danger)', color: '#fff', borderRadius: 20,
            fontSize: 10, fontWeight: 700, minWidth: 17, height: 17, lineHeight: '17px',
            padding: '0 4px', textAlign: 'center',
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', insetInlineEnd: 0, zIndex: 1200,
          width: 'min(360px, calc(100vw - 32px))', maxHeight: 420, overflowY: 'auto',
          background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        }}>
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid var(--border-light)',
            fontSize: 13, fontWeight: 700, color: 'var(--heading)',
            position: 'sticky', top: 0, background: 'var(--card-bg)',
          }}>
            {t('notif.title')}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-subtle)' }}>
              {t('notif.empty')}
            </div>
          ) : items.map(n => (
            <div key={n._id}
              onClick={() => { const to = notifLink(n); if (to) { setOpen(false); navigate(to); } }}
              style={{
                padding: '11px 16px', borderBottom: '1px solid var(--border-light)',
                display: 'flex', gap: 10, alignItems: 'flex-start',
                background: n.readAt ? 'transparent' : 'rgba(46,117,182,0.06)',
                cursor: notifLink(n) ? 'pointer' : 'default',
              }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{ICONS[n.type] || '🔔'}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                {n.type === 'announcement' && n.title && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{n.title}</div>
                )}
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.45, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                  {notifText(n, t)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 3 }}>{timeAgo(n.createdAt, t)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
